/**
 * Crypto deposit through NOWPayments, all on this page: the client enters a USD amount and picks a
 * coin (those selected in the NOWPayments dashboard); we show the address, exact amount, QR code and
 * the time left on the fixed rate, then follow the payment live until it is credited.
 */
import { useEffect, useRef, useState } from 'react';
import { cryptoCoin, fmt, type CryptoDepositConfig, type CryptoPayment } from '@platform/shared';
import { NumInput } from '../../../components/NumInput';
import { cx } from '../../../components/ui';
import { UsdtChip } from '../../../components/UsdtInfo';
import { api } from '../../../lib/api';
import { navigate, useSearch } from '../../../lib/router';
import { useLoad, useVersion } from '../../../stores/account';
import { AIcon, Alert, Card, Chip, CopyButton, errMsg, Field, H1, H2, List, Notice, PillButton, PillLink, QrCode, Row, when } from '../kit';
import { CoinIcon, coinLabel, DEFAULT_COINS, ordered } from './coins';
import { CoinPicker } from './CoinPicker';
import { DepositTicket } from './DepositTicket';
import { CancelPayment, mmss, OPEN, resumable, STATUS, useFollowPayment, useNow } from './payment';

export function NowPaymentsDeposit({ cfg }: { cfg: CryptoDepositConfig }) {
  const v = useVersion('deposit');
  const { data: list, reload: reloadList } = useLoad<CryptoPayment[]>('/api/deposits/crypto', [v]);
  // Opened from the dashboard's Move funds card: ?coin=usdttrc20&amount=500 (read once).
  const search = useSearch();
  const linked = useRef(
    (() => {
      const q = new URLSearchParams(search);
      const a = q.get('amount') ?? '';
      return { coin: (q.get('coin') ?? '').toLowerCase(), amount: /^\d+(\.\d{1,2})?$/.test(a) ? a : '' };
    })(),
  ).current;
  const [amount, setAmount] = useState(linked.amount);
  const coins = ordered(cfg.coins.length ? cfg.coins : DEFAULT_COINS);
  const [coin, setCoin] = useState<string>(coins.includes(linked.coin) ? linked.coin : (coins[0] ?? ''));
  // The real list arrives after the first render: keep the choice if offered, else take the first (USDT on ERC20).
  useEffect(() => {
    if (!coins.includes(coin)) setCoin(coins[0] ?? '');
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [coins.join()]);
  const [active, setActive] = useState<CryptoPayment | null>(null);
  const [ticket, setTicket] = useState<CryptoPayment | null>(null);
  const [busy, setBusy] = useState(false);
  const [msg, setMsg] = useState<{ ok: boolean; text: string } | null>(null);

  // Pick up a payment still waiting (page reloaded mid-payment), but not one the client left.
  const [left, setLeft] = useState<string[]>([]);
  useEffect(() => {
    if (active || !list) return;
    const open = list.find((p) => !left.includes(p.id) && resumable(p));
    if (open) setActive(open);
  }, [list, active, left]);

  useFollowPayment(active, setActive);

  const min = Number(cfg.minUsd);
  const amt = Number(amount) || 0;
  const start = async () => {
    if (amt < min) return setMsg({ ok: false, text: amt ? `The minimum crypto deposit is $${fmt(cfg.minUsd, 0)}.` : `Enter the amount you want to deposit (at least $${fmt(cfg.minUsd, 0)}).` });
    setBusy(true);
    setMsg(null);
    try {
      const p = await api.post<CryptoPayment>('/api/deposits/crypto', { amount, coin });
      setActive(p);
      void reloadList();
    } catch (x) {
      setMsg({ ok: false, text: errMsg(x) });
    } finally {
      setBusy(false);
    }
  };
  // From the card with a coin and an amount: create the payment straight away (once), unless a
  // payment is still open (then that one is shown). The link is dropped so a reload doesn't repeat it.
  const auto = useRef(false);
  useEffect(() => {
    if (auto.current || !cfg.enabled || !list || !linked.coin || !linked.amount) return;
    auto.current = true;
    navigate('/account/deposit?method=crypto', { replace: true });
    if (!list.some(resumable) && !active && coins.includes(linked.coin)) void start();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [cfg.enabled, list]);

  const reset = () => {
    if (active) setLeft((l) => [...l, active.id]);
    setActive(null);
    setMsg(null);
    void reloadList();
  };

  return (
    <div className="grid grid-cols-[minmax(0,1fr)] items-start gap-8 xl:grid-cols-[minmax(0,1fr)_minmax(0,500px)] 2xl:grid-cols-[minmax(0,1fr)_minmax(0,564px)]">
      <Card>
        <div className="flex flex-wrap items-center gap-3">
          <H1>Deposit with crypto</H1>
          <UsdtChip />
        </div>
        <p className="m-0 mt-2 text-[16px] text-muted md:text-[17px]">Enter the amount in USDT and choose your coin. We show the address and the exact amount to send right here.</p>
        {active ? (
          <PaymentPanel p={active} onReset={reset} />
        ) : (
          <div className="mt-8 flex flex-col gap-7">
            <Field label="Amount (USDT)" htmlFor="np-amount" hint={`Minimum $${fmt(cfg.minUsd, 0)}. Credited to your live account in USDT.`}>
              <div className="flex overflow-hidden rounded-[14px] bg-track">
                <NumInput
                  id="np-amount"
                  className="num min-w-0 flex-1 bg-transparent px-3.5 py-3 text-[22px] font-medium outline-none"
                  placeholder="0.00"
                  value={amount}
                  onValue={(x) => /^\d*\.?\d{0,2}$/.test(x) && setAmount(x)}
                />
                <span className="num grid place-items-center border-l border-line-strong px-4 text-[15px] font-semibold text-muted">USDT</span>
              </div>
            </Field>
            <CoinPicker coins={coins} value={coin} onChange={setCoin} />
            {!cfg.enabled && (
              <Alert tone="info">
                <b>Crypto payments are being switched on.</b> Try again in a little while, or deposit by wire transfer or card in the meantime.
              </Alert>
            )}
            <Notice msg={msg} onClose={() => setMsg(null)} />
            <PillButton size="lg" busy={busy} disabled={!cfg.enabled || !coin} onClick={() => void start()}>
              Get payment address
            </PillButton>
            <p className="m-0 text-[13px] text-muted">Payments are processed by NOWPayments. You have 45 minutes to send the coins; network fees are paid by you.</p>
          </div>
        )}
      </Card>

      <div className="flex min-w-0 flex-col gap-8">
        <Card>
          <H2>Recent crypto deposits</H2>
          <div className="mt-6">
            <List empty="No crypto deposits yet">
              {(list ?? []).map((p) => (
                <button key={p.id} type="button" onClick={() => setTicket(p)} aria-label={`Open the ticket for the $${fmt(p.amountUsd, 2)} ${coinLabel(p.payCurrency)} deposit`} className="group -mx-5 block w-[calc(100%+40px)] border-b border-line px-5 text-left first:rounded-t-[20px] last:rounded-b-[20px] last:border-b-0 hover:bg-track focus-visible:bg-track">
                  <Row
                    mono
                    icon={<CoinIcon code={p.payCurrency} size={30} />}
                    title={`${p.status === 'finished' ? '+' : ''}$${fmt(p.creditedUsd ?? p.amountUsd, 2)}`}
                    sub={`${coinLabel(p.payCurrency)} · ${when(p.createdAt)}`}
                    right={
                      <span className="flex shrink-0 items-center gap-2">
                        <Chip tone={STATUS[p.status][1]}>{STATUS[p.status][0]}</Chip>
                        <AIcon name="right" size={16} className="text-muted group-hover:text-fg" />
                      </span>
                    }
                  />
                </button>
              ))}
            </List>
          </div>
        </Card>
        {ticket && <DepositTicket p={ticket} onClose={() => setTicket(null)} onChanged={() => void reloadList()} />}
        <Card className="flex flex-wrap items-center gap-5">
          <div className="min-w-[200px] flex-1">
            <h3 className="m-0 text-[20px] font-semibold">Payment not showing?</h3>
            <p className="m-0 mt-1.5 text-[16px] text-muted">Most coins confirm in 5 to 30 minutes. Open the deposit above for its details, or after 1 hour contact support with the transaction hash.</p>
          </div>
          <PillLink to="/account/support?topic=funding">Contact support</PillLink>
        </Card>
      </div>
    </div>
  );
}

/** The payment screen: amount, QR, address, countdown, status and Cancel. Shared with the dashboard pop-up. */
export function PaymentPanel({ p, onReset, className = 'mt-8' }: { p: CryptoPayment; onReset: () => void; className?: string }) {
  const open = OPEN.includes(p.status);
  const now = useNow(p.status === 'waiting');
  const left = p.expiresAt ? Math.max(0, Date.parse(p.expiresAt) - now) : null;
  const k = cryptoCoin(p.payCurrency);
  const expired = p.status === 'expired' || (p.status === 'waiting' && left === 0);
  const step = p.status === 'waiting' ? 0 : open ? 1 : p.status === 'finished' ? 3 : -1;

  if (p.status === 'finished')
    return (
      <div className={cx(className, 'flex flex-col items-start gap-5')}>
        <Notice msg={{ ok: true, text: `Payment received. $${fmt(p.creditedUsd ?? p.amountUsd, 2)} was added to your live account.` }} />
        <PillButton onClick={onReset}>Make another deposit</PillButton>
      </div>
    );
  if (p.status === 'partially_paid')
    return (
      <div className={cx(className, 'flex flex-col items-start gap-5')}>
        <Alert>
          <b>Part of your payment arrived.</b> We received {p.actuallyPaid ?? 'less than'} {k.symbol} of the {p.payAmount} {k.symbol} due. Our team will credit what arrived in USDT and contact you.
        </Alert>
        <PillButton kind="secondary" onClick={onReset}>
          Make another deposit
        </PillButton>
      </div>
    );
  if (expired || p.status === 'failed' || p.status === 'refunded' || p.status === 'cancelled')
    return (
      <div className={cx(className, 'flex flex-col items-start gap-5')}>
        <Alert>
          {p.status === 'cancelled'
            ? 'You cancelled this payment. Do not send coins to its address.'
            : p.status === 'refunded'
            ? 'This payment was refunded.'
            : p.status === 'failed'
              ? 'This payment failed. Nothing was taken from your wallet by us; try again.'
              : 'This payment address expired. If you already sent coins, contact support with the transaction hash. Otherwise start again for a fresh rate.'}
        </Alert>
        <PillButton onClick={onReset}>Start again</PillButton>
      </div>
    );

  return (
    <div className={cx(className, 'flex flex-col gap-6')}>
      <div className="flex flex-wrap items-baseline justify-between gap-3">
        <span className="flex items-center gap-3 text-[20px] font-semibold md:text-[22px]">
          <CoinIcon code={p.payCurrency} size={32} />
          <span>
            Send <span className="num">{p.payAmount}</span> {k.symbol}
          </span>
        </span>
        {p.status === 'waiting' && left !== null && (
          <span className="num text-[14px] font-medium text-warn">
            {mmss(left)} left
          </span>
        )}
      </div>
      <div className="flex flex-wrap gap-6 rounded-[20px] border border-line p-5 md:gap-8 md:p-6">
        {p.payAddress && <QrCode value={p.payAddress} size={184} label={`${coinLabel(p.payCurrency)} payment address QR code`} />}
        <div className="flex min-w-[220px] flex-1 flex-col gap-4">
          <span className="text-[15px] font-semibold">{coinLabel(p.payCurrency)} address</span>
          <code className="num rounded-[12px] bg-track px-4 py-3.5 text-[14px] break-all md:text-[15px]">{p.payAddress}</code>
          <div className="flex flex-wrap gap-2">
            <CopyButton value={p.payAddress ?? ''} size="sm">
              Copy address
            </CopyButton>
            <CopyButton value={p.payAmount ?? ''} size="sm" kind="secondary">
              Copy amount
            </CopyButton>
          </div>
          {p.payExtraId && (
            <div className="flex flex-col gap-2">
              <span className="text-[14px] font-semibold text-down">Memo / tag (required)</span>
              <code className="num rounded-[12px] bg-track px-4 py-3 text-[14px] break-all">{p.payExtraId}</code>
              <CopyButton value={p.payExtraId} size="sm" kind="secondary">
                Copy memo
              </CopyButton>
            </div>
          )}
        </div>
      </div>
      <dl className="num m-0 grid grid-cols-2 gap-4 text-[15px]">
        <div>
          <dt className="font-sans text-muted">You deposit</dt>
          <dd className="m-0">${fmt(p.amountUsd, 2)}</dd>
        </div>
        <div>
          <dt className="font-sans text-muted">Network fee</dt>
          <dd className="m-0 font-sans">Paid by you</dd>
        </div>
      </dl>
      <ol className="m-0 flex list-none flex-wrap gap-2 p-0" aria-label="Payment status">
        {['Waiting for your payment', 'Confirming on the network', 'Credited'].map((t, i) => (
          <li key={t} className={cx('rounded-full px-3 py-1.5 text-[12px] font-semibold', i < step ? 'bg-up-bg text-up' : i === step ? 'bg-accent text-white' : 'bg-track text-muted')}>
            {t}
          </li>
        ))}
      </ol>
      <Alert>
        Send exactly <b className="num">{p.payAmount} {k.symbol}</b>
        {k.network ? (
          <>
            {' '}
            on the <b>{k.network}</b> network
          </>
        ) : null}{' '}
        to this address, once. Other coins or networks are lost. The address is for this payment only.
      </Alert>
      <CancelPayment p={p} onDone={onReset} />
    </div>
  );
}
